import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { t } from '../lib/i18n';
import { disablePush, enablePush, pushStatus, type PushStatus } from '../lib/push';
import { BellIcon, CloseIcon } from './icons';

const DISMISSED = 'bedly.pushPromptDismissed';
/** A "not now" is respected for two weeks, then asked once more. */
const QUIET_DAYS = 14;

const dismissedRecently = (): boolean => {
  try {
    const at = Number(localStorage.getItem(DISMISSED) ?? 0);
    return Date.now() - at < QUIET_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

/**
 * Invites a signed-in person to get notifications on this phone or computer.
 *
 * `banner` sits at the top of the pages where people wait on others (account,
 * host, messages, notifications) and can be put off for a while; `settings` is
 * the permanent control, with a test and an off switch.
 */
export default function PushPrompt({ variant }: { variant: 'banner' | 'settings' }) {
  const { user, toast, reportError } = useApp();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => variant === 'banner' && dismissedRecently());

  useEffect(() => {
    let active = true;
    void pushStatus().then((value) => active && setStatus(value));
    return () => {
      active = false;
    };
  }, []);

  if (!user || status === null || hidden) return null;
  // The banner only ever asks; once on, blocked or impossible, it steps aside.
  if (variant === 'banner' && status !== 'off' && status !== 'install') return null;

  const turnOn = async () => {
    setBusy(true);
    try {
      const next = await enablePush();
      setStatus(next);
      if (next === 'on') {
        await api.post('/push/test');
        toast(t('Notifications activées sur cet appareil.'), 'ok');
      } else if (next === 'denied') {
        toast(t('Les notifications ont été refusées pour Bedly.'), 'error');
      }
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
      setStatus('off');
      toast(t('Notifications désactivées sur cet appareil.'), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const response = await api.post<{ sent: number }>('/push/test');
      toast(
        response.sent > 0 ? t('Notification de test envoyée.') : t('Aucun appareil n’a reçu la notification.'),
        response.sent > 0 ? 'ok' : 'error',
      );
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED, String(Date.now()));
    } catch {
      // Hidden for this visit at least.
    }
    setHidden(true);
  };

  const explanation =
    status === 'install'
      ? t('Sur iPhone : touchez Partager, puis « Sur l’écran d’accueil ». Ouvrez Bedly depuis la nouvelle icône et activez les notifications.')
      : status === 'denied'
        ? t('Les notifications sont bloquées pour Bedly dans ce navigateur. Autorisez-les dans les réglages du site (le cadenas à côté de l’adresse), puis rechargez la page.')
        : status === 'unsupported'
          ? t('Ce navigateur ne permet pas les notifications. Essayez Chrome, Edge, Firefox ou Safari à jour.')
          : status === 'on'
            ? t('Activées sur cet appareil : demandes, réservations et messages arrivent même quand Bedly est fermé.')
            : t('Soyez prévenu sur ce téléphone ou cet ordinateur : nouvelle demande, réservation confirmée, nouveau message — même quand Bedly est fermé.');

  return (
    <div className={`push-prompt push-prompt--${variant} ${status === 'on' ? 'push-prompt--on' : ''}`}>
      <span className="push-prompt__icon" aria-hidden>
        <BellIcon />
      </span>
      <div className="push-prompt__body">
        <span className="strong">
          {status === 'on' ? t('Notifications sur cet appareil') : t('Ne ratez aucune demande')}
        </span>
        <span className="small muted">{explanation}</span>
        <div className="push-prompt__actions">
          {status === 'off' ? (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void turnOn()}>
              {busy ? t('Activation…') : t('Activer les notifications')}
            </button>
          ) : null}
          {status === 'on' && variant === 'settings' ? (
            <>
              <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void sendTest()}>
                {t('Envoyer un test')}
              </button>
              <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={() => void turnOff()}>
                {t('Désactiver sur cet appareil')}
              </button>
            </>
          ) : null}
        </div>
      </div>
      {variant === 'banner' ? (
        <button type="button" className="push-prompt__close" aria-label={t('Plus tard')} onClick={dismiss}>
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
}
